import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
    RiArrowDropDownLine,
    RiCheckLine,
    RiUserLine,
    RiSearchLine,
    RiBuildingLine,
    RiCloseLine
} from 'react-icons/ri';

const TenantSelectDropdown = ({ tenants, value, onChange, disabled, t, isDarkMode, i18n }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const isRTL = i18n?.language === 'ar' || i18n?.language === 'ku';

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Clear search term when closed
    useEffect(() => {
        if (!isOpen) {
            setSearchTerm('');
        }
    }, [isOpen]);

    const filteredTenants = useMemo(() => {
        if (!searchTerm.trim()) return tenants;
        const lowerSearch = searchTerm.toLowerCase();
        return tenants.filter(tenant => {
            const name = (tenant.name || '').toLowerCase();
            const apt = (tenant.apartmentNumber || '').toString().toLowerCase();
            const building = (tenant.buildingName || '').toLowerCase();
            const phone = (tenant.phone || '').toLowerCase();

            return name.includes(lowerSearch) ||
                apt.includes(lowerSearch) ||
                building.includes(lowerSearch) ||
                phone.includes(lowerSearch);
        });
    }, [tenants, searchTerm]);

    const selectedTenant = tenants.find(t => t.id === value);

    const inputClasses = `w-full px-4 py-2.5 rounded-lg border text-start flex justify-between items-center transition-colors focus:outline-none focus:ring-2 focus:ring-green-500
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}
    `;

    const dropdownClasses = `absolute z-50 w-full mt-1 rounded-xl shadow-xl border overflow-hidden flex flex-col max-h-[300px]
        ${isDarkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}
    `;

    const itemClasses = (isSelected) => `w-full text-start px-4 py-2.5 border-b last:border-0 hover:bg-opacity-80 transition-colors flex items-center justify-between
        ${isDarkMode ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-100 hover:bg-green-50'}
        ${isSelected ? (isDarkMode ? 'bg-gray-700' : 'bg-green-50') : ''}
    `;

    const handleSelect = (id) => {
        onChange({ target: { name: 'tenantId', value: id } });
        setIsOpen(false);
    };

    return (
        <div className="relative w-full" ref={dropdownRef} dir={isRTL ? 'rtl' : 'ltr'}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setIsOpen(!isOpen)}
                className={inputClasses}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <div className="flex items-center truncate">
                    <RiUserLine className={`${isRTL ? 'ml-2' : 'mr-2'} ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                    <span className="truncate">
                        {selectedTenant
                            ? `${selectedTenant.name} - ${selectedTenant.apartmentNumber ? `${t('apt')} ${selectedTenant.apartmentNumber}` : t('no_apartment')}`
                            : t('select_tenant') || 'Select Tenant'}
                    </span>
                </div>
                <RiArrowDropDownLine size={24} className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} ${isOpen ? 'transform rotate-180' : ''} transition-transform`} />
            </button>

            {isOpen && !disabled && (
                <div className={dropdownClasses} role="listbox">
                    {/* Search Header */}
                    <div className={`p-2 border-b ${isDarkMode ? 'border-gray-700 bg-zinc-800' : 'border-gray-200 bg-gray-50'}`}>
                        <div className="relative">
                            <RiSearchLine className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} size={18} />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder={t('search_tenants') || 'Search tenants...'}
                                autoFocus
                                className={`w-full ${isRTL ? 'pr-9 pl-4' : 'pl-9 pr-4'} py-2 text-sm rounded-lg outline-none transition-shadow focus:ring-2 focus:ring-green-500/50 
                                    ${isDarkMode ? 'bg-zinc-900 text-white placeholder-gray-500' : 'bg-white text-gray-900 placeholder-gray-400 border border-gray-300'}`}
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600`}
                                >
                                    <RiCloseLine size={16} />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="overflow-y-auto flex-1">
                        {filteredTenants.length === 0 ? (
                            <div className={`p-4 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {t('no_tenants_found') || 'No tenants found'}
                            </div>
                        ) : (
                            filteredTenants.map(tenant => (
                                <button
                                    key={tenant.id}
                                    type="button"
                                    onClick={() => handleSelect(tenant.id)}
                                    className={itemClasses(value === tenant.id)}
                                    role="option"
                                    aria-selected={value === tenant.id}
                                >
                                    <div className="flex flex-col items-start truncate">
                                        <span className={`font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                            {tenant.name}
                                        </span>
                                        <div className={`text-xs mt-0.5 flex items-center gap-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            <RiBuildingLine size={12} />
                                            <span className="truncate">
                                                {tenant.buildingName ? `${tenant.buildingName} • ` : ''}
                                                {tenant.apartmentNumber ? `${t('apt')} ${tenant.apartmentNumber}` : t('no_apartment')}
                                            </span>
                                        </div>
                                    </div>
                                    {value === tenant.id && <RiCheckLine size={18} className="text-green-500 flex-shrink-0" />}
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TenantSelectDropdown;
